import React, { useEffect, useState } from 'react';
import Layout from '../../../components/Layout/Layout';
import Modal from '../../../components/Modal/Modal';
import StatusBadge from '../../../components/StatusBadge/StatusBadge';
import api from '../../../api/axios';
import { fmtDate } from '../../../utils/download';

function RecurrenceEditor({ training }) {
  const [enabled, setEnabled] = useState(!!training.is_recurring);
  const [intervalVal, setIntervalVal] = useState(training.recur_interval || 1);
  const [unit, setUnit] = useState(training.recur_unit || 'years');
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  async function save() {
    setSaving(true);
    try {
      await api.put(`/trainings/${training.id}/recurrence`, {
        isRecurring: enabled,
        interval: parseInt(intervalVal, 10) || 1,
        unit,
      });
      setDirty(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      alert(err.response?.data?.message || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: '0.6rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={enabled}
          onChange={e => { setEnabled(e.target.checked); setDirty(true); }}
          style={{ accentColor: 'var(--primary)' }}
        />
        🔁 Wiederholend
      </label>
      {enabled && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem' }}>
          <span>alle</span>
          <input
            type="number"
            min={1}
            value={intervalVal}
            onChange={e => { setIntervalVal(e.target.value); setDirty(true); }}
            style={{ width: 64, padding: '0.3rem 0.4rem', border: '1px solid var(--gray)', borderRadius: 6 }}
          />
          <select
            value={unit}
            onChange={e => { setUnit(e.target.value); setDirty(true); }}
            style={{ padding: '0.3rem 0.4rem', border: '1px solid var(--gray)', borderRadius: 6 }}
          >
            <option value="days">Tage</option>
            <option value="months">Monate</option>
            <option value="years">Jahre</option>
          </select>
        </div>
      )}
      {dirty && (
        <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
          {saving ? 'Speichern...' : 'Speichern'}
        </button>
      )}
      {saved && <span style={{ color: 'var(--success)', fontSize: '0.8rem' }}>✓ Gespeichert</span>}
    </div>
  );
}

// Lizenzstatus einer Zuweisung (AM-07/09/11), eine Aussage je Zeile
function LicenseInfo({ a, compact = false }) {
  return (
    <div className="cell-meta">
      <span>{a.started ? `Begonnen am ${fmtDate(a.started_at)}` : 'Nicht begonnen'}</span>
      {!compact && (a.nonRenewalScheduled
        ? <span className="is-end">Läuft aus am {fmtDate(a.finalEnd)}</span>
        : a.periodEnd && <span>Periode bis {fmtDate(a.periodEnd)}, verlängert sich automatisch</span>)}
      {a.stornoPossible && <span className="is-ok">Kostenfreie Rücknahme bis {fmtDate(a.stornoUntil)}</span>}
      {!compact && a.moduleCancelled && !a.nonRenewalScheduled && <span className="is-warn">Modul gekündigt</span>}
    </div>
  );
}

export default function CustomerTrainings() {
  const [trainings, setTrainings] = useState([]);
  const [users, setUsers] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [assignModal, setAssignModal] = useState(null); // training object
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [deadline, setDeadline] = useState('');
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [transferFrom, setTransferFrom] = useState(null); // assignment, das übertragen wird
  const [msg, setMsg] = useState('');

  function reload() {
    api.get('/trainings').then(res => setTrainings(res.data));
    api.get('/users').then(res => setUsers(res.data.filter(u => u.status === 'active')));
    api.get('/user-trainings').then(res => setAssignments(res.data));
  }

  useEffect(() => { reload(); }, []);

  function openAssign(training) {
    setAssignModal(training);
    setSelectedUsers([]);
    setDeadline('');
    setMsg('');
    setTransferFrom(null);
  }

  const activeAssignments = assignments.filter(a => !a.accessEnded);

  // Aktive Zuweisung pro Mitarbeiter für die aktuell geöffnete Schulung
  const assignmentByUser = {};
  if (assignModal) {
    activeAssignments
      .filter(a => a.trainingId === assignModal.id)
      .forEach(a => { assignmentByUser[a.userId] = a; });
  }

  async function handleUnassign(assignment) {
    const text = assignment.stornoPossible
      ? `Zuweisung für "${assignment.userName}" zurücknehmen?\n\n` +
        `Der Kurs wurde noch nicht begonnen und die Zuweisung liegt weniger als 14 Tage zurück: Die Rücknahme ist kostenfrei, bereits berechnete Raten werden gutgeschrieben (AGB § 5 (6) e).`
      : `Kurslizenz für "${assignment.userName}" sofort entfernen?\n\n` +
        `Hinweis: Der Zugriff endet sofort. Die Vergütung läuft gemäß AGB § 5 (6) d bis zum Laufzeitende (${fmtDate(assignment.nonRenewalEffective || assignment.periodEnd)}) weiter. ` +
        'Wenn der Zugriff bis zum Laufzeitende bestehen bleiben soll, nutzen Sie stattdessen "Nicht verlängern".';
    if (!window.confirm(text + '\n\nSie erhalten eine Bestätigung per E-Mail.')) return;
    setBusyId(assignment.id);
    try {
      const res = await api.delete(`/user-trainings/${assignment.id}`);
      setMsg(res.data?.message || 'Zuweisung entfernt.');
      reload();
    } catch (err) {
      alert(err.response?.data?.message || 'Fehler beim Entfernen');
    } finally {
      setBusyId(null);
    }
  }

  async function handleNonRenewal(assignment) {
    const ok = window.confirm(
      `Kurslizenz "${assignment.trainingTitle}" für ${assignment.userName} zur Nicht-Verlängerung vormerken?\n\n` +
      `Die Lizenz endet am ${fmtDate(assignment.nonRenewalEffective)}` +
      (assignment.nonRenewalDeadline ? ` (spätester Vormerkungstermin für das laufende Periodenende: ${fmtDate(assignment.nonRenewalDeadline)})` : '') +
      '. Der Zugriff bleibt bis dahin bestehen, die Vormerkung kann bis dahin widerrufen werden (AGB § 5 (6) d).\n\nSie erhalten eine Bestätigung per E-Mail.'
    );
    if (!ok) return;
    setBusyId(assignment.id);
    try {
      const res = await api.post(`/user-trainings/${assignment.id}/non-renewal`);
      setMsg(res.data?.message || 'Nicht-Verlängerung vorgemerkt.');
      reload();
    } catch (err) {
      alert(err.response?.data?.message || 'Fehler beim Vormerken');
    } finally {
      setBusyId(null);
    }
  }

  async function handleRevokeNonRenewal(assignment) {
    if (!window.confirm(`Vormerkung für "${assignment.userName}" aufheben? Die Kurslizenz verlängert sich dann wieder automatisch.`)) return;
    setBusyId(assignment.id);
    try {
      const res = await api.post(`/user-trainings/${assignment.id}/non-renewal/revoke`);
      setMsg(res.data?.message || 'Vormerkung aufgehoben.');
      reload();
    } catch (err) {
      alert(err.response?.data?.message || 'Fehler beim Aufheben');
    } finally {
      setBusyId(null);
    }
  }

  async function handleTransfer(targetUser) {
    if (!transferFrom) return;
    const ok = window.confirm(
      `Kurslizenz "${assignModal?.title}" von ${transferFrom.userName} auf ${targetUser.name} übertragen? ` +
      'Laufzeit und Preis der Lizenz bleiben unverändert (AGB § 5 (6) e). Sie erhalten eine Bestätigung per E-Mail.'
    );
    if (!ok) return;
    try {
      const res = await api.post(`/user-trainings/${transferFrom.id}/transfer`, { toUserId: targetUser.id });
      setMsg(res.data?.message || 'Kurslizenz übertragen.');
      setTransferFrom(null);
      reload();
    } catch (err) {
      alert(err.response?.data?.message || 'Fehler bei der Übertragung');
    }
  }

  async function handleCancelModule(t) {
    const ok = window.confirm(
      `Modul "${t.title}" kündigen?\n\n` +
      `Die Kündigung wirkt zum ${fmtDate(t.moduleCancelEffective)} (Periodenende, Frist 1 Monat gemäß AGB § 9 (2)). ` +
      'Alle Kurslizenzen dieses Moduls enden spätestens zu diesem Zeitpunkt; der Zugriff bleibt bis dahin bestehen. ' +
      'Die Kündigung kann über die Plattform nicht zurückgenommen werden.\n\nSie erhalten eine Bestätigung per E-Mail.'
    );
    if (!ok) return;
    try {
      const res = await api.post(`/trainings/${t.id}/cancel-module`);
      setMsg(res.data?.message || 'Modul gekündigt.');
      reload();
    } catch (err) {
      alert(err.response?.data?.message || 'Fehler bei der Kündigung');
    }
  }

  function toggleUser(id) {
    setSelectedUsers(prev => prev.includes(id) ? prev.filter(u => u !== id) : [...prev, id]);
  }

  function toggleAll() {
    const selectable = users.filter(u => !assignmentByUser[u.id]).map(u => u.id);
    setSelectedUsers(prev => prev.length === selectable.length ? [] : selectable);
  }

  async function handleAssign(e) {
    e.preventDefault();
    if (!selectedUsers.length) return;
    if (!window.confirm('Hinweis: Durch diese Zuweisung wird das Kontingent kostenpflichtig erhöht (siehe AGB und Einzelauftrag). Die Laufzeit umfasst den angebrochenen Monat plus 11 volle Monate; innerhalb von 14 Tagen ist eine kostenfreie Rücknahme möglich, solange der Kurs nicht begonnen wurde.')) return;
    setSaving(true);
    try {
      const res = await api.post('/user-trainings', {
        userIds: selectedUsers,
        trainingId: assignModal.id,
        deadline: deadline || null,
      });
      setMsg(res.data.message || `${res.data.count} Mitarbeiter wurden zugewiesen.`);
      setAssignModal(null);
      reload();
    } catch (err) {
      setMsg(err.response?.data?.message || 'Fehler');
    } finally {
      setSaving(false);
    }
  }

  // Lizenzaktionen leben in der Tabelle "Kurslizenzen"; das Zuweisungs-Modal bietet nur die Übertragung an
  function AssignmentActions({ a }) {
    return (
      <div className="actions-row">
        {a.nonRenewalScheduled ? (
          <button type="button" className="btn btn-ghost btn-sm" disabled={busyId === a.id}
            onClick={() => handleRevokeNonRenewal(a)}>
            Vormerkung aufheben
          </button>
        ) : (
          <button type="button" className="btn btn-ghost btn-sm" disabled={busyId === a.id}
            onClick={() => handleNonRenewal(a)}>
            Nicht verlängern
          </button>
        )}
        <button type="button" className="btn btn-ghost btn-ghost-danger btn-sm" disabled={busyId === a.id}
          onClick={() => handleUnassign(a)}>
          {busyId === a.id ? 'Bitte warten…' : (a.stornoPossible ? 'Zurücknehmen' : 'Entfernen')}
        </button>
      </div>
    );
  }

  return (
    <Layout title="Schulungen">
      {msg && <div className="alert alert-success" style={{ marginBottom: '1rem' }}>{msg}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1.25rem' }}>
        {trainings.map(t => (
          <div key={t.id} className="card" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <h3 style={{ fontWeight: 700, fontSize: '1rem' }}>{t.title}</h3>
              <span style={{ background: 'rgba(94,211,209,0.15)', color: 'var(--primary-dark)', fontWeight: 700, fontSize: '0.8rem', padding: '0.2rem 0.6rem', borderRadius: 999 }}>{t.passing_score}%</span>
            </div>
            {t.description && <p style={{ fontSize: '0.875rem', color: 'var(--gray-dark)' }}>{t.description}</p>}
            <p style={{ fontSize: '0.8rem', color: 'var(--gray-dark)' }}>📝 {t.questionCount} Fragen</p>
            <RecurrenceEditor training={t} />
            <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: '0.6rem', fontSize: '0.75rem', color: 'var(--gray-dark)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
              {t.moduleCancelled ? (
                <span className="badge badge-failed">Modul gekündigt zum {fmtDate(t.final_end)}</span>
              ) : (
                <>
                  <span>Modul-Periode bis {fmtDate(t.modulePeriodEnd)}</span>
                  <button className="btn btn-ghost btn-ghost-danger btn-sm" onClick={() => handleCancelModule(t)}>Modul kündigen</button>
                </>
              )}
            </div>
            <button className="btn btn-primary btn-sm" onClick={() => openAssign(t)} style={{ marginTop: 'auto' }}>
              Schulung zuweisen
            </button>
          </div>
        ))}
        {trainings.length === 0 && <p style={{ color: 'var(--gray-dark)' }}>Keine Schulungen verfügbar.</p>}
      </div>

      <div className="card" style={{ marginTop: '1.5rem' }}>
        <h2 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '0.25rem' }}>Kurslizenzen</h2>
        <p style={{ fontSize: '0.8rem', color: 'var(--gray-dark)', marginBottom: '1rem', maxWidth: '72ch' }}>
          Laufzeit: angebrochener Zuweisungsmonat plus 11 volle Monate, danach automatische Verlängerung um 12 Monate.
          Eine Nicht-Verlängerung ist bis einen Monat vor Periodenende vormerkbar. Übertragung (im Zuweisungsdialog) und kostenfreie Rücknahme sind nur möglich, solange der Kurs nicht begonnen wurde (AGB § 5 (6) d/e).
        </p>
        <div className="table-container">
          <table>
            <thead>
              <tr><th>Mitarbeiter</th><th>Schulung</th><th>Status</th><th>Lizenz</th><th>Aktionen</th></tr>
            </thead>
            <tbody>
              {activeAssignments.map(a => (
                <tr key={a.id}>
                  <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{a.userName}</td>
                  <td>{a.trainingTitle}</td>
                  <td><StatusBadge status={a.status} /></td>
                  <td><LicenseInfo a={a} /></td>
                  <td><AssignmentActions a={a} /></td>
                </tr>
              ))}
              {activeAssignments.length === 0 && <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--gray-dark)', padding: '1.5rem' }}>Keine aktiven Kurslizenzen</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Modal isOpen={!!assignModal} onClose={() => setAssignModal(null)} title={`Zuweisen: ${assignModal?.title}`} size="lg">
        <form onSubmit={handleAssign}>
          <div className="form-group">
            <label>Deadline (optional)</label>
            <input type="date" value={deadline} onChange={e => setDeadline(e.target.value)} min={new Date().toISOString().split('T')[0]} />
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <label style={{ fontWeight: 600, fontSize: '0.9rem' }}>Mitarbeiter auswählen</label>
              <button type="button" className="btn btn-ghost btn-sm" onClick={toggleAll}>
                {selectedUsers.length > 0 && selectedUsers.length === users.filter(u => !assignmentByUser[u.id]).length ? 'Alle abwählen' : 'Alle auswählen'}
              </button>
            </div>
            {transferFrom && (
              <div className="alert alert-success" style={{ marginBottom: '0.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' }}>
                <span>Kurslizenz von <strong>{transferFrom.userName}</strong> übertragen: Zielnutzer wählen.</span>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setTransferFrom(null)}>Abbrechen</button>
              </div>
            )}
            <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid #e2e2e2', borderRadius: 8 }}>
              {users.map(u => {
                const assignment = assignmentByUser[u.id];
                const assigned = !!assignment;
                return (
                  <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem 1rem', cursor: assigned ? 'default' : 'pointer', borderBottom: '1px solid #f0f0f0', transition: 'background 0.15s' }}
                    onMouseEnter={e => { if (!assigned) e.currentTarget.style.background = 'var(--light)'; }}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <input type="checkbox" checked={selectedUsers.includes(u.id)} onChange={() => toggleUser(u.id)} disabled={assigned} style={{ accentColor: 'var(--primary)' }} />
                    <div style={{ opacity: assigned ? 0.75 : 1, flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 500, fontSize: '0.9rem' }}>{u.name}</div>
                      <div style={{ fontSize: '0.8rem', color: 'var(--gray-dark)' }}>{u.email}</div>
                      {assigned && <LicenseInfo a={assignment} compact />}
                    </div>
                    {assigned && !transferFrom && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
                        <span className="badge badge-in_progress">Zugewiesen</span>
                        {assignment.transferable && (
                          <button type="button" className="btn btn-ghost btn-sm"
                            onClick={e => { e.preventDefault(); e.stopPropagation(); setTransferFrom(assignment); }}>
                            Übertragen
                          </button>
                        )}
                      </span>
                    )}
                    {!assigned && transferFrom && (
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        style={{ marginLeft: 'auto' }}
                        onClick={e => { e.preventDefault(); e.stopPropagation(); handleTransfer(u); }}
                      >
                        → Hierhin übertragen
                      </button>
                    )}
                  </label>
                );
              })}
              {users.length === 0 && <p style={{ padding: '1rem', color: 'var(--gray-dark)', textAlign: 'center' }}>Keine aktiven Mitarbeiter vorhanden.</p>}
            </div>
            <p style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: 'var(--gray-dark)' }}>{selectedUsers.length} von {users.length} ausgewählt</p>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setAssignModal(null)}>Abbrechen</button>
            <button type="submit" className="btn btn-primary btn-sm" disabled={saving || !selectedUsers.length}>
              {saving ? 'Zuweisen...' : 'Zuweisen'}
            </button>
          </div>
        </form>
      </Modal>
    </Layout>
  );
}
